import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { IconProgressHelp, IconRestore, IconHandMove } from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/hintUsed.png";
import unIntento from "../../assets/un-intento-blue.png"
import dosIntentos from "../../assets/dos-intentos-blue.png"

function Scenario4Radioterapia({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.RADIOTERAPIA_O_PROSTATECTOMIA;
  const SCENARIO_ID = SCENARIOS.SCENARIO_4;

  // ⚙️ Puntuación / intentos
  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWERS = ["Bajo", "Bajo"];
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  // Estado drag & drop
  const items = [
    { id: "alto-1", label: "Alto" },
    { id: "alto-2", label: "Alto" },
    { id: "bajo-1", label: "Bajo" },
    { id: "bajo-2", label: "Bajo" },
  ];
  const [slots, setSlots] = useState([null, null]);

  // Estado general
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);            // 1..2
  const [pendingScore, setPendingScore] = useState(0);  // acumulado a enviar al final

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con la suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO4_FIN,
        correct: correctFinal,
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO4_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO4_FIN);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  function resetValues() {
    setSlots([null, null]);
  }

  const useHint = async () => {
    const hint =
      'En el contexto de la enfermedad metastásica, la radioterapia a la próstata primaria se reserva para un subgrupo muy específico. Piensa en qué característica (volumen y riesgo) define a quienes NO se benefician del tratamiento local agresivo.';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "blue" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará una sola vez en el primer intento que compute puntos
    }
  };

  function checkAnswer() {
    if (slots.includes(null)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes completar los espacios" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 blue">Debes completar ambos espacios</div>`,
      });
      return;
    }

    const labelsInSlots = slots.map((id) => items.find((it) => it.id === id)?.label);
    const isCorrect =
      labelsInSlots[0] === CORRECT_ANSWERS[0] &&
      labelsInSlots[1] === CORRECT_ANSWERS[1];

    modalConfirm({ estasseguro, color: "blue" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1º o 2º intento)
        const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "El control con radioterapia en la próstata está recomendado en pacientes CPSCm...",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:450px' />
                     <div class="txt32 bold pt-4">Ganaste ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "blue",
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: restamos -5 (y -3 si pista aquí). NO bloqueamos ni revelamos qué está mal.
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "El control con radioterapia a la próstata está recomendado en pacientes CPSCm...",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                     <div class="txt28 bold pt-3">La combinación no es correcta. ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Te queda <b>1</b> intento. Revisa tu respuesta y vuelve a intentarlo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "blue",
        }).then(() => {
          setPendingScore(newScore);
          setAttempt(attempt + 1);
          // No bloqueamos ni reseteamos los slots (pueden ajustarlos manualmente).
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí aplica pista por 1ª vez). Enviamos.
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "El control con radioterapia a la próstata está recomendado en pacientes CPSCm...",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                   <div class="txt32 bold pt-4">Has agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "blue",
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  // 🔄 Drag & Drop
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const newSlots = [...slots];

    // Pool -> Slot
    if (source.droppableId === "pool" && destination.droppableId.startsWith("slot")) {
      const slotIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[slotIndex] = draggableId;
    }

    // Slot -> Pool
    if (source.droppableId.startsWith("slot") && destination.droppableId === "pool") {
      const slotIndex = parseInt(source.droppableId.replace("slot-", ""));
      newSlots[slotIndex] = null;
    }

    // Slot -> Slot
    if (source.droppableId.startsWith("slot") && destination.droppableId.startsWith("slot")) {
      const fromIndex = parseInt(source.droppableId.replace("slot-", ""));
      const toIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[toIndex] = draggableId;
      newSlots[fromIndex] = null;
    }

    setSlots(newSlots);
  };

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  return (
    <div className="h-100">
      <div className="container-fluid h-100 px-5 d-flex flex-column justify-content-start bgCircleBlue2 pt-2">
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Frase con slots */}
          <div className="row">
            <div className="col-12">
              <h3 className="txt30 blue bold" style={{ backgroundColor: "#FFFFFFAA", borderRadius: "10px", paddingTop: "5px", paddingBottom: "5px"}}>
                Afortunadamente Manuel tuvo respuesta parcial al tratamiento con Apalutamida​. ¿Consideras ofrecer algún tratamiento a la próstata?​
              </h3>
              <p
                className="txt30 blue bold text-center"
                style={{
                  borderRadius: "10px",
                  backgroundColor: "#FFFFFFAA",
                  paddingBottom: "15px",
                  lineHeight: "1.5",
                }}
              >
                El control local de radioterapia a la próstata está recomendado en pacientes con CPSm con{" "}
                <Droppable droppableId="slot-0">
                  {(provided) => (
                    <span
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        borderBottom: "2px dashed #999",
                        minWidth: "80px",
                        display: "inline-block",
                        margin: "0 4px",
                        minHeight: "40px",
                      }}
                    >
                      {slots[0] ? (
                        <Draggable draggableId={slots[0]} index={0}>
                          {(provided) => {
                            const item = items.find((it) => it.id === slots[0]);
                            return (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-2 py-1 rounded bgBlue text-white"
                              >
                                {item?.label}
                              </span>
                            );
                          }}
                        </Draggable>
                      ) : (
                        "_____"
                      )}
                      {provided.placeholder}
                    </span>
                  )}
                </Droppable>{" "}
                volumen y{" "}
                <Droppable droppableId="slot-1">
                  {(provided) => (
                    <span
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        borderBottom: "2px dashed #999",
                        minWidth: "80px",
                        display: "inline-block",
                        margin: "0 4px",
                        minHeight: "40px",
                      }}
                    >
                      {slots[1] ? (
                        <Draggable draggableId={slots[1]} index={1}>
                          {(provided) => {
                            const item = items.find((it) => it.id === slots[1]);
                            return (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-2 py-1 rounded bgBlue text-white"
                              >
                                {item?.label}
                              </span>
                            );
                          }}
                        </Draggable>
                      ) : (
                        "_____"
                      )}
                      {provided.placeholder}
                    </span>
                  )}
                </Droppable>{" "}
                riesgo
              </p>
            </div>
          </div>

          
        <div className="d-flex justify-content-end mb-1">
          <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
        </div>


          <div className="row">
            <div className="col-6">
              <div className="d-flex justify-content-end">
                <h5 className="mb-4 txt32 text-left my-2 blue">
                  <IconHandMove size={40} color="#69D0FF" stroke={1} /> Mantén presionado el valor unos instantes para moverlo al lugar correcto en la frase incompleta.<br/>
                  Si quieres cambiar la respuesta antes de validar, vuelve a arrastrar el valor a su zona o toca el botón Reset <IconRestore size={30} color="#69D0FF" stroke={2} />
                </h5>
              </div>
            </div>
            <div className="col-6">
              {/* Pool de palabras (solo las que NO estén en slots) */}
              <Droppable droppableId="pool" direction="horizontal">
                {(provided) => (
                  <div
                    className="position-relative d-flex flex-column mt-2"
                    style={{ backgroundColor: "#69D0FF44", borderRadius: "10px", padding: "20px", height: "240px" }}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <div className="txt24 mb-4">Valores posibles:</div>
                    <div className="d-flex">
                      {items
                        .filter((it) => !slots.includes(it.id))
                        .map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided) => (
                              <div
                                className="mx-2 px-3 py-2 my-2 rounded shadow-sm txt22 bold cursor-pointer dragWidth text-center"
                                style={{ backgroundColor: "#69D0FF", color: "white" }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {item.label}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>

                    <button
                      className="btn-main bold shadow position-absolute bgBlue"
                      onClick={resetValues}
                      style={{ left: "25px", bottom: "20px" }}
                    >
                      <IconRestore size={30} color="white" stroke={2} />
                    </button>
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>

        <div className="d-flex justify-content-between mt-2">
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaBlue}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="blue"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario4Radioterapia);
